<template>
    <div class="mx-auto max-w-screen-md space-y-8 pb-6">
        <prime-message
            :closable="false"
            severity="info"
            class="!my-0 !rounded-none"
        >
            <template #container>
                <div
                    class="flex flex-col items-start justify-between gap-4 p-5 sm:flex-row sm:items-center"
                >
                    <div
                        class="flex items-center gap-3 text-lg font-medium text-gray-900"
                    >
                        <i
                            class="pi pi-clock shrink-0 text-xl"
                            aria-hidden="true"
                        />
                        <span>{{ resetMessage }}</span>
                    </div>
                    <prime-button
                        type="button"
                        :label="t('checkoutSuccess.resetKioskNow')"
                        severity="secondary"
                        class="shrink-0"
                        @click="$emit('reset')"
                    />
                </div>
            </template>
        </prime-message>

        <checkout-success-kiosk-display
            :checkout-result-data="checkoutResultData"
            :completed-message="completedMessage"
            :staff-activation-required-message="staffActivationRequiredMessage"
        />
    </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'
import CheckoutSuccessKioskDisplay from '~/components/Checkout/CheckoutSuccessKioskDisplay.vue'
import PrimeMessage from 'primevue/message'
import PrimeButton from 'primevue/button'
import { computed, onMounted, ref } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import type { KioskSuccessData } from '~/composables/use-kiosk-success'
import { usePurchaseAnalytics } from '~/composables/analytics/purchase-event'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    checkoutResultData: KioskSuccessData
    completedMessage: string
    staffActivationRequiredMessage: string
}>()

const emit = defineEmits<{
    (e: 'reset'): void
}>()

const { t } = useI18n({ useScope: 'global' })
const { emitPurchaseEvent } = usePurchaseAnalytics()

useHead({ title: t('success') })

const resetCounter = ref<number | null>(10)
const resetMessage = computed(() => {
    const secondsRemaining = resetCounter.value ?? 0
    return t(
        'checkoutSuccess.kioskResetMessage',
        { seconds: secondsRemaining },
        secondsRemaining
    )
})

useIntervalFn(() => {
    if (resetCounter.value !== null) {
        resetCounter.value -= 1
        if (resetCounter.value < 1) {
            resetCounter.value = null
            emit('reset')
        }
    }
}, 1000)

onMounted(() => {
    if (props.checkoutResultData.analytics !== undefined) {
        emitPurchaseEvent(props.checkoutResultData.analytics)
    }
})
</script>
